import { MenuPortalTargetOptions } from "@src/components/deprecated/SelectInputField/SelectInputField";
import { PropertyData } from "../../Property/PropertyData";
import { UnitData } from "../../Property/Units/UnitData";
import { DataObjectFieldComparator } from "../DataObjects/DataObjectFieldComparator";
import { DataObjectType } from "../DataObjects/DataObjectHelper";
import { Timestamp } from "firebase/firestore";

export interface DecodeStringContext {
  stringValues: { [key: string]: string };
  format: "excel" | "other";
}
export interface InputElementContext {
  propertyID?: string;
  unit?: UnitData;
  minWidth?: string | number;
  width?: string | number;
  height?: string | number;
  disabled?: boolean;
  menuPortalTarget?: MenuPortalTargetOptions;
  comparator?: DataObjectFieldComparator;
  dataCy?: string;
}

export abstract class DataObjectField<FieldType, T extends DataObjectType> {
  key: string;
  title: string;
  isArray: boolean;
  iconName?: string;

  dataGetter: (data: T) => FieldType | FieldType[] | undefined;
  equalComparator?: (a: FieldType | undefined, b: FieldType | undefined) => boolean;
  includesComparator?: (a: FieldType, b: FieldType) => boolean;

  singleInputElement?: (
    value: FieldType | undefined,
    setValue: (value: FieldType | undefined) => void,
    context?: InputElementContext,
    useCustomSort?: boolean
  ) => JSX.Element;

  multiInputElement?: (
    value: FieldType[] | undefined,
    setValue: (value: FieldType[] | undefined) => void,
    context?: InputElementContext,
    useCustomSort?: boolean
  ) => JSX.Element;

  getColor?(value: FieldType | undefined): string | undefined;

  inputElement = (
    value: FieldType | FieldType[] | undefined,
    setValue: (value: FieldType | FieldType[] | undefined) => void,
    context?: InputElementContext,
    useCustomSort?: boolean
  ) => {
    if (context?.comparator && (context.comparator === "rolling-back" || context.comparator === "rolling-forward")) {
      return this.singleInputElement?.(Array.isArray(value) ? undefined : value, setValue, context);
    }

    return this.isArray
      ? this.multiInputElement?.(Array.isArray(value) ? value : undefined, setValue, context, useCustomSort)
      : this.singleInputElement?.(Array.isArray(value) ? undefined : value, setValue, context, useCustomSort);
  };

  loadData?(value: FieldType): Promise<any>;
  loadRawData?(value: any): Promise<any>;

  abstract isValueValid(value: any): boolean;
  abstract encodeString: (value: FieldType) => string;
  abstract decodeString: (value: string, context?: DecodeStringContext) => FieldType | undefined;
  abstract encode: (value: FieldType) => any;
  abstract decode: (value: any) => FieldType | undefined;

  fullEncode = (value: FieldType | FieldType[]) => {
    if (this.fullIsEncodedValueValid(value)) return value;

    return Array.isArray(value)
      ? value.map((val) => (this.isValueValid(val) ? this.encode(val) : null))
      : this.isValueValid(value)
        ? this.encode(value)
        : null;
  };

  fullDecode = (value: any): FieldType[] | FieldType | undefined => {
    if (this.fullIsValueValid(value)) return value;
    return Array.isArray(value) ? value.map((val) => this.decode(val)).filter((val) => val !== undefined) : this.decode(value);
  };

  fullIsValueValid = (value: any): value is this["isArray"] extends true ? FieldType[] : FieldType => {
    return this.isArray ? Array.isArray(value) && value.every((val) => this.isValueValid(val)) : this.isValueValid(value);
  };

  constructor(key: string, title: string, dataGetter: (data: T) => FieldType | FieldType[] | undefined, iconName?: string, isArray: boolean = false) {
    this.key = key;
    this.title = title;
    this.dataGetter = dataGetter;
    this.iconName = iconName;
    this.isArray = isArray;
  }

  getStringRepresentation = (value: FieldType | FieldType[] | undefined, displayAsRaw?: boolean): string => {
    if (value === undefined || value === null) return "";
    if (Array.isArray(value)) {
      return value.map((val) => this.getStringRepresentation(val, displayAsRaw) ?? "").join(", ");
    } else {
      const decoded = this.getValue(value);
      if (decoded !== undefined && decoded !== null) return this.getReadableStringRepresentation(decoded, displayAsRaw);
      return "";
    }
  };

  protected getValue = (value: any): FieldType | undefined => {
    if (this.isValueValid(value)) return value;
    const decoded = this.decode(value);
    if (decoded !== undefined) return decoded;
    return undefined;
  };

  abstract getReadableStringRepresentation(value: FieldType, displayAsRaw?: boolean): string;

  abstract allComparators: DataObjectFieldComparator[];

  isEncodedValueValid = (value: any): boolean => {
    return (
      typeof value === "string" ||
      typeof value === "number" ||
      typeof value === "boolean" ||
      value instanceof Timestamp ||
      value instanceof Date ||
      value === null
    );
  };
  fullIsEncodedValueValid = (value: any): boolean => {
    return Array.isArray(value) ? value.every((val) => this.isEncodedValueValid(val)) : this.isEncodedValueValid(value);
  };
}
